import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { LEADERBOARD_DATA, AVATAR_GRADIENTS } from '@/mocks/leaderboard';

const FILTERS = ['Today', 'This Week', 'All Time'] as const;
const TOP_MEDALS = ['🥇', '🥈', '🥉'];

type FilterType = (typeof FILTERS)[number];

type LeaderboardEntry = {
  rank: number;
  name: string;
  score: number;
  badge: string;
  streak: number;
  avatar: string;
  date?: string;
  isYou?: boolean;
};

type StoredResult = {
  challengeId: string;
  challengeDate: string;
  classification: string;
  severity: string;
  nextAction: string;
  score: number;
};

function getUserBadge(score: number): string {
  if (score >= 25) return 'Perfect Diagnostician';
  if (score >= 20) return 'Incident Engineer';
  if (score >= 10) return 'Failure Analyst';
  return 'Log Reader';
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isWithinLast7Days(dateStr?: string): boolean {
  if (!dateStr) return true;

  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return false;

  const now = new Date();
  const diff = now.getTime() - target.getTime();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

  return diff <= sevenDaysMs;
}

function isToday(dateStr?: string): boolean {
  if (!dateStr) return true;

  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return false;

  return sameDay(target, new Date());
}

function getStoredHistory(): StoredResult[] {
  try {
    const historyRaw = localStorage.getItem('explainerror_history');
    if (historyRaw) {
      const parsed = JSON.parse(historyRaw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }

    const singleRaw = localStorage.getItem('explainerror_result');
    if (singleRaw) {
      const parsed = JSON.parse(singleRaw);
      return [parsed];
    }

    return [];
  } catch {
    return [];
  }
}

function getCurrentStreak(): number {
  const value =
    localStorage.getItem('currentStreak') ??
    localStorage.getItem('explainerror_streak') ??
    '0';

  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getBestStreak(): number {
  const value =
    localStorage.getItem('bestStreak') ??
    localStorage.getItem('explainerror_best_streak') ??
    '0';

  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export default function LeaderboardPage() {
  const [filter, setFilter] = useState<FilterType>('This Week');
  const userRowRef = useRef<HTMLDivElement | null>(null);

  const currentStreak = getCurrentStreak();
  const bestStreak = getBestStreak();

  const leaderboardRows = useMemo(() => {
    const rows: LeaderboardEntry[] = [...LEADERBOARD_DATA];
    const storedHistory = getStoredHistory();

    const filteredHistory = storedHistory.filter((item) => {
      if (filter === 'Today') return isToday(item.challengeDate);
      if (filter === 'This Week') return isWithinLast7Days(item.challengeDate);
      return true;
    });

    if (filteredHistory.length > 0) {
      const bestUserScore = Math.max(...filteredHistory.map((item) => item.score));
      const latestResult = filteredHistory[filteredHistory.length - 1];

      rows.push({
        rank: rows.length + 1,
        name: 'You',
        score: bestUserScore,
        badge: getUserBadge(bestUserScore),
        streak: currentStreak,
        avatar: 'YO',
        date: latestResult.challengeDate,
        isYou: true,
      });
    }

    return rows
      .sort((a, b) => b.score - a.score)
      .map((entry, index) => ({
        ...entry,
        rank: index + 1,
      }));
  }, [filter, currentStreak]);

  useEffect(() => {
    if (userRowRef.current) {
      userRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [leaderboardRows]);

  const yourEntry = leaderboardRows.find((entry) => entry.isYou);
  const topScore = leaderboardRows[0]?.score ?? 0;
  const playerCount = leaderboardRows.length;

  return (
    <div className="min-h-screen bg-bg-base font-sans">
      <Navbar />

      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bg-panel border border-accent/30 text-accent text-xs font-mono uppercase tracking-widest mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              Rankings
            </div>

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
                  Leaderboard
                </h1>
                <p className="text-text-secondary text-sm mt-2">
                  See who reads failures best and where you rank.
                </p>
              </div>

              <div className="flex items-center gap-1 bg-bg-panel border border-border-subtle rounded-full px-1 py-1">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFilter(f)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filter === f
                        ? 'bg-accent text-bg-base font-semibold'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 animate-fade-in">
            <div className="bg-bg-panel border border-border-subtle rounded-xl p-5">
              <div className="text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
                Top score
              </div>
              <div className="text-2xl font-bold text-accent">{topScore}</div>
              <p className="text-sm text-text-secondary mt-1">
                Highest score for {filter.toLowerCase()}.
              </p>
            </div>

            <div className="bg-bg-panel border border-border-subtle rounded-xl p-5">
              <div className="text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
                Players
              </div>
              <div className="text-2xl font-bold text-accent">{playerCount}</div>
              <p className="text-sm text-text-secondary mt-1">
                Engineers shown on this board.
              </p>
            </div>

            <div className="bg-bg-panel border border-border-subtle rounded-xl p-5">
              <div className="text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
                Current streak
              </div>
              <div className="text-2xl font-bold text-accent">{currentStreak}</div>
              <p className="text-sm text-text-secondary mt-1">
                Your active daily incident streak.
              </p>
            </div>

            <div className="bg-bg-panel border border-border-subtle rounded-xl p-5">
              <div className="text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
                Best streak
              </div>
              <div className="text-2xl font-bold text-accent">{bestStreak}</div>
              <p className="text-sm text-text-secondary mt-1">
                Your longest streak so far.
              </p>
            </div>
          </div>

          <div className="bg-bg-panel border border-border-subtle rounded-xl overflow-hidden animate-fade-in">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 border-b border-border-subtle bg-bg-surface">
              <span className="col-span-1 text-xs font-mono text-text-muted uppercase">
                Rank
              </span>
              <span className="col-span-5 text-xs font-mono text-text-muted uppercase">
                Engineer
              </span>
              <span className="col-span-3 text-xs font-mono text-text-muted uppercase">
                Badge
              </span>
              <span className="col-span-2 text-xs font-mono text-text-muted uppercase text-right">
                Score
              </span>
              <span className="col-span-1 text-xs font-mono text-text-muted uppercase text-right">
                Streak
              </span>
            </div>

            {leaderboardRows.map((entry, index) => (
              <div
                key={`${entry.name}-${index}-${entry.score}`}
                ref={entry.isYou ? userRowRef : null}
                className={`grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 px-5 py-4 border-b border-border-subtle items-center transition-colors ${
                  entry.isYou
                    ? 'bg-accent/5 border-accent/20'
                    : 'hover:bg-bg-elevated'
                }`}
              >
                <div className="sm:col-span-1">
                  <span
                    className={`font-mono font-bold text-sm ${
                      entry.rank <= 3 ? 'text-accent' : 'text-text-muted'
                    }`}
                  >
                    {entry.rank <= 3 ? TOP_MEDALS[entry.rank - 1] : entry.rank}
                  </span>
                </div>

                <div className="sm:col-span-5 flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-full bg-gradient-to-br ${
                      AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length]
                    } flex items-center justify-center text-xs font-bold text-white`}
                  >
                    {entry.avatar}
                  </div>

                  <div>
                    <div className="text-sm font-medium text-text-primary flex items-center gap-2">
                      <span>{entry.name}</span>
                      {entry.isYou && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-accent/15 text-accent border border-accent/20">
                          You
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-text-muted sm:hidden">{entry.badge}</div>
                  </div>
                </div>

                <div className="hidden sm:block sm:col-span-3">
                  <span className="px-2 py-1 rounded bg-bg-elevated border border-border-subtle text-xs font-mono text-text-muted">
                    {entry.badge}
                  </span>
                </div>

                <div className="sm:col-span-2 text-right">
                  <span className="font-mono font-bold text-accent text-sm">
                    {entry.score}
                  </span>
                </div>

                <div className="hidden sm:block sm:col-span-1 text-right">
                  <span className="text-xs font-mono text-warn">{entry.streak}d</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/challenge"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent hover:bg-accent-hover text-bg-base font-semibold rounded-md text-sm"
            >
              Play today and claim your rank
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}